import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiSellerDiscount } from '@wix/da-papi-types';
import { getDiscountedPrice } from '../utils/deviations';
import { formatDollars } from '../utils/formatting';
import DiscountLabel, { DiscountLabelProps } from '../DiscountLabel';

import s from './PriceWithDiscount.scss';

interface Props extends Pick<
  DiscountLabelProps,
  'size' | 'reversedTheme' | 'textVariant' | 'opaque'
> {
  prefix?: React.ReactNode;
  infix?: React.ReactNode;
  className?: string;
  dollarAmount: number | string;
  eligibleDiscount?: PapiSellerDiscount;
  variant?: 'exclusive' | 'regular';
}

const formatDollarsOptions = {
  useGrouping: true,
};

const PriceWithDiscount: React.FC<Props> = ({
  className,
  dollarAmount,
  eligibleDiscount,
  size = 'medium',
  variant = 'regular',
  prefix,
  infix,
  ...discountLabelProps
}) => {
  const { t } = useTranslation();

  dollarAmount = parseFloat(`${dollarAmount}`);
  const { price, discountedPrice } = getDiscountedPrice(dollarAmount, eligibleDiscount);
  const formattedDollarAmount = formatDollars(price, formatDollarsOptions);
  const formattedDollarAmountAfterDiscount = formatDollars(discountedPrice, formatDollarsOptions);

  return (
    <div className={c(s['root'], s[size], s[variant], className)}>
      {prefix}
      {eligibleDiscount && size !== 'small' && (
        <>
          {t('priceWithDiscount.price', {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            price: <div className={s['price']}>{formattedDollarAmountAfterDiscount}</div>,

            priceBeforeDiscount: <div className={c(s['old-price'])}>{formattedDollarAmount}</div>,
          })}
          {infix}
          <DiscountLabel
            className={s['discount-label']}
            discount={eligibleDiscount}
            size={'small'}
            {...discountLabelProps}
          />
        </>
      )}
      {(!eligibleDiscount || size === 'small') &&
        t('priceWithDiscount.price', {
          postProcess: ['reactPostprocessor', 'featureBrancher'],
          price: <div className={s['price']}>{formattedDollarAmount}</div>,
        })}
    </div>
  );
};
PriceWithDiscount.displayName = 'PriceWithDiscount';

export default PriceWithDiscount;
